import { useState } from 'react';
import ImageCard from '@components/common/ImageCard';
import { DATA_BASE_URL } from '@lib/constants';

const PrisonChessInitiativeSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const prisonPhotos = [
    {
      src: `${DATA_BASE_URL}/data/social_impact/prison_chess/prison_chess_image.webp`,
      alt: 'Prison Chess Initiative - Teaching chess to inmates',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/prison_chess/inmate_chess_1.webp`,
      alt: 'Inmates playing chess',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/prison_chess/inmate_chess_2.webp`,
      alt: 'Chess training session with inmates',
    },
    {
      src: `${DATA_BASE_URL}/data/social_impact/prison_chess/inmate_simultaneous_game.webp`,
      alt: 'Simultaneous chess exhibition for inmates',
    },
  ];

  const certificates = [
    {
      name: 'San Fernando Jail Recognition',
      pdf: `${DATA_BASE_URL}/data/social_impact/certificates/san_fernando_jail_recognition.pdf`,
      description: 'Official recognition for teaching chess to inmates at San Fernando Jail',
    },
    {
      name: 'General Santos City Jail Certificate',
      pdf: `${DATA_BASE_URL}/data/social_impact/certificates/2022_general_santos_city_jail_certificate.pdf`,
      description: 'Certificate of appreciation from General Santos City Jail (2022)',
    },
    {
      name: 'AFC Day Prisoners Note',
      pdf: `${DATA_BASE_URL}/data/social_impact/prison_chess/afc_day_prisoners_note_by_srihaan.pdf`,
      description: 'Note I wrote for AFC Day celebration with prisoners',
    },
  ];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Prison Chess Initiative
          </h2>
          <p
            className="text-lg md:text-xl leading-relaxed"
            style={{ color: 'var(--color-subheading)' }}
          >
            Bringing hope and strategic thinking to incarcerated individuals in the Philippines by teaching chess as a tool for mindset transformation and rehabilitation.
          </p>
        </div>

        {/* Story Section */}
        <div
          className="max-w-4xl mx-auto mb-12 p-8 rounded-lg"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-card-border)',
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
        >
          <h3
            className="text-2xl font-semibold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            The Story
          </h3>
          <div
            className="space-y-4 text-base md:text-lg leading-relaxed"
            style={{ color: 'var(--color-card-text)' }}
          >
            <p>
              In 2021, I began a transformative initiative to teach chess to inmates in Philippine jails,
              including San Fernando Jail and General Santos City Jail. What started as a small program grew into
              an international movement.
            </p>
            <p>
              The inmates I coached went on to compete in the 2nd Intercontinental Chess Championship for Prisoners,
              where they competed against players from 46 countries. Their success brought recognition from the
              Philippine Senate, national and international media, and international chess organizations.
            </p>
            <p>
              Beyond winning games, this initiative revealed how chess can instill structure, discipline,
              and hope in those seeking a second chance. Remarkably, three of my teammates were granted early release for good conduct and none have returned to prison, defying a cycle of repeat offenses that was once common. It proved that transformation is possible, one move at a time.
            </p>
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="mb-12">
          <h3
            className="text-2xl font-semibold mb-6 text-center"
            style={{ color: 'var(--color-heading)' }}
          >
            Behind the Bars, Beyond the Board
          </h3>
          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {prisonPhotos.map((photo, index) => (
              <div
                key={index}
                className="cursor-pointer transform transition-transform hover:scale-105"
                onClick={() => setSelectedImage(photo)}
              >
                <ImageCard
                  src={photo.src}
                  alt={photo.alt}
                  aspectRatio="aspect-square"
                  className="hover:shadow-lg"
                />
                <p
                  className="mt-2 text-sm text-center"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {photo.alt}
                </p>
              </div>
            ))}
          </div>
          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-4 pb-2" style={{ minWidth: 'min-content' }}>
              {prisonPhotos.map((photo, index) => (
                <div
                  key={index}
                  className="cursor-pointer flex-shrink-0"
                  style={{ width: '280px' }}
                  onClick={() => setSelectedImage(photo)}
                >
                  <ImageCard
                    src={photo.src}
                    alt={photo.alt}
                    aspectRatio="aspect-square"
                    className="shadow-md"
                  />
                  <p
                    className="mt-2 text-sm text-center"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {photo.alt}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recognition Certificates */}
        <div>
          <h3
            className="text-2xl font-semibold mb-6 text-center"
            style={{ color: 'var(--color-heading)' }}
          >
            Official Recognition
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {certificates.map((cert, index) => (
              <a
                key={index}
                href={cert.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div
                  className="p-6 rounded-lg transition-all duration-200 h-full"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: 'var(--color-card-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-accent-5)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-card-border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* PDF Icon */}
                  <div
                    className="w-12 h-12 rounded-lg mb-4 flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-accent-2)' }}
                  >
                    <svg
                      className="w-6 h-6"
                      style={{ color: 'var(--color-accent-7)' }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 18h12V6h-4V2H4v16zm-2 1V0h12l4 4v16H2v-1z" />
                    </svg>
                  </div>

                  <h4
                    className="text-lg font-semibold mb-2"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {cert.name}
                  </h4>
                  <p
                    className="text-sm mb-3"
                    style={{ color: 'var(--color-card-text)' }}
                  >
                    {cert.description}
                  </p>
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--color-accent-6)' }}
                  >
                    View Certificate →
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-screen">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-screen object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PrisonChessInitiativeSection;
